import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type {
  AffiliationHistoryRow,
  AffiliationSummary,
  DeveloperRepMe,
  LatestAffiliationSummary,
} from '~/server/utils/developerRep/account';

export type { AffiliationHistoryRow, AffiliationSummary, DeveloperRepMe, LatestAffiliationSummary };

export const DEVELOPER_REP_ME_KEY = ['developer-rep', 'me'] as const;
const HISTORY_KEY = ['developer-rep', 'history'] as const;

export const fetchDeveloperRepMe = (token: string, origin = '') =>
  $fetch<DeveloperRepMe>(`${origin}/api/developer-rep/me`, { headers: { Authorization: `Bearer ${token}` } });

// Shared by the profile switcher, the developer layout, the middleware and every rep page.
export const useDeveloperRepAccountQuery = () => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: DEVELOPER_REP_ME_KEY,
    queryFn: () => fetchDeveloperRepMe(authStore.token as string),
    enabled: computed(() => !!authStore.token),
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  });
};

export const useDeveloperRepAccountHistoryQuery = () => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: HISTORY_KEY,
    queryFn: () =>
      $fetch<{ success: boolean; data: AffiliationHistoryRow[] }>('/api/developer-rep/settings/history', {
        headers: { Authorization: `Bearer ${authStore.token}` },
      }),
    enabled: computed(() => !!authStore.token),
    select: (res) => res?.data ?? [],
    retry: false,
    throwOnError: false,
  });
};

export type DeveloperRepApplicationInput = {
  developerId: number;
  fullName: string;
  position: string;
  corporateEmail?: string;
  contactPhone: string;
  supportingInfo?: string;
  files?: File[];
};

const invalidateAccount = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: DEVELOPER_REP_ME_KEY }),
    queryClient.invalidateQueries({ queryKey: HISTORY_KEY }),
  ]);

export const useDeveloperRepAccountApply = () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeveloperRepApplicationInput) => {
      const body = new FormData();
      body.append('developerId', String(input.developerId));
      body.append('fullName', input.fullName);
      body.append('position', input.position);
      body.append('contactPhone', input.contactPhone);
      if (input.corporateEmail) body.append('corporateEmail', input.corporateEmail);
      if (input.supportingInfo) body.append('supportingInfo', input.supportingInfo);
      for (const f of input.files ?? []) body.append('file', f, f.name);
      return $fetch<{ success: boolean; data: LatestAffiliationSummary }>('/api/developer-rep/application', {
        method: 'POST',
        body,
        headers: { Authorization: `Bearer ${authStore.token}` },
      });
    },
    onSettled: () => invalidateAccount(queryClient),
  });
};

export const useDeveloperRepAccountLeave = () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      $fetch<{ success: boolean }>('/api/developer-rep/settings/leave', {
        method: 'POST',
        headers: { Authorization: `Bearer ${authStore.token}` },
      }),
    onSettled: () => invalidateAccount(queryClient),
  });
};
